/**
 * Signboards data — NoJo Enterprise outdoor advertising story.
 *
 * Hub: /signboards
 * Detail: /signboards/:slug
 *
 * Structure: regions (Okinawa, Yokosuka, ...) each containing one or more
 * signboards. Each signboard has bilingual title/description, location,
 * route, status, a hero photo, and a gallery of photos.
 *
 * Photos live under /signboards/<slug>/ in the public folder.
 *
 * Owner directive: site is built so new regions/boards can be added
 * with just a data-file entry — no UI changes required as we expand
 * across Japan.
 */

// Helper: build photo URLs for a folder by listing the static known names.
// We baked these counts in when extracting from the source PDFs. If new
// photos are added under public/signboards/<slug>/, add them to the
// `photos` array below.
function oy(n) {
  const s = String(n).padStart(3, "0");
  const t = String(n - 1).padStart(3, "0");
  return `/signboards/oyama/oy-${s}-${t}.jpg`;
}
function bf(n) {
  const s = String(n).padStart(3, "0");
  const t = String(n - 1).padStart(3, "0");
  return `/signboards/b36a-camp-foster/bf-${s}-${t}.jpg`;
}
function b13(n) {
  const s = String(n).padStart(3, "0");
  const t = String(n - 1).padStart(3, "0");
  return `/signboards/b13/b13-${s}-${t}.jpg`;
}

export const SIGNBOARDS = [
  {
    slug: "b13-route-58",
    region: "okinawa",
    title: {
      en: "B-13 — Route 58",
      ja: "B-13 — 国道58号線",
    },
    location: {
      en: "Route 58 corridor, Okinawa",
      ja: "沖縄県 国道58号線沿い",
    },
    route: "Route 58",
    status: "live",
    nearest: {
      en: "Central Okinawa stretch",
      ja: "沖縄本島中部",
    },
    summary: {
      en: "B-13 position along the Route 58 corridor — extending JapanETCcard visibility across the busiest commuter and tourist stretch of central Okinawa.",
      ja: "国道58号線沿いのB-13区画。沖縄本島中部、通勤・観光双方で最も交通量の多い区間でJapanETCcardの認知を広げます。",
    },
    photos: Array.from({ length: 16 }, (_, i) => b13(i + 1)),
    streetView: {
      url: "https://www.google.com/maps/@26.2732311,127.7396904,3a,43.6y,11.31h,104.76t/data=!3m7!1e1!3m5!1ssyfWzBkQ5XyvM6omk6yKUQ!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-14.759017379565037%26panoid%3DsyfWzBkQ5XyvM6omk6yKUQ%26yaw%3D11.311117909328322!7i16384!8i8192",
      pano: "syfWzBkQ5XyvM6omk6yKUQ",
      lat: 26.2732311,
      lng: 127.7396904,
      heading: 11.311117909328322,
      pitch: 14.759017379565037,
      capturedLabel: {
        en: "Live · Google Street View",
        ja: "ライブ · Google ストリートビュー",
      },
    },
  },
  {
    slug: "b36a-camp-foster",
    region: "okinawa",
    title: {
      en: "B-36A — Camp Foster Route 58",
      ja: "B-36A — キャンプ・フォスター 国道58号線",
    },
    location: {
      en: "Camp Foster, Okinawa",
      ja: "沖縄県 キャンプ・フォスター周辺",
    },
    route: "Route 58",
    status: "live",
    nearest: {
      en: "Camp Foster gate area",
      ja: "キャンプ・フォスター ゲート付近",
    },
    summary: {
      en: "B-36A position on Route 58 alongside Camp Foster — the largest U.S. Marine Corps installation on Okinawa. High visibility to SOFA-status drivers and Japanese commuters passing the base perimeter daily.",
      ja: "沖縄最大の米海兵隊基地、キャンプ・フォスター沿いの国道58号線に設置されたB-36A区画。SOFA関係者と毎日基地沿いを通勤する日本人ドライバーに対し、高い視認性を持っています。",
    },
    photos: Array.from({ length: 8 }, (_, i) => bf(i + 1)),
    streetView: {
      url: "https://www.google.com/maps/@26.2924155,127.7589911,3a,16.1y,359.9h,104.97t/data=!3m7!1e1!3m5!1speppE6XzhVNwoLP8TiD48Q!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-14.97351569218786%26panoid%3DpeppE6XzhVNwoLP8TiD48Q%26yaw%3D359.9021484433396!7i16384!8i8192",
      pano: "peppE6XzhVNwoLP8TiD48Q",
      lat: 26.2924155,
      lng: 127.7589911,
      heading: 359.9021484433396,
      pitch: 14.97351569218786,
      capturedLabel: {
        en: "Live · Google Street View",
        ja: "ライブ · Google ストリートビュー",
      },
    },
  },
  {
    slug: "oyama-route-58",
    region: "okinawa",
    title: {
      en: "Oyama — Route 58",
      ja: "大山 — 国道58号線",
    },
    location: {
      en: "Oyama, Ginowan, Okinawa",
      ja: "沖縄県宜野湾市大山",
    },
    route: "Route 58",
    status: "live",
    nearest: {
      en: "Near MCAS Futenma",
      ja: "普天間飛行場近郊",
    },
    summary: {
      en: "JapanETCcard signboard on Route 58 — Okinawa's main north-south artery — visible to drivers heading between Naha, Camp Foster, and the central island bases.",
      ja: "沖縄本島の南北をつなぐ主要幹線、国道58号線沿いのJapanETCcard看板。那覇とキャンプ・フォスター、本島中部の基地エリアを行き来するドライバーに視認されます。",
    },
    photos: Array.from({ length: 16 }, (_, i) => oy(i + 1)),
    streetView: {
      url: "https://www.google.com/maps/@26.2833499,127.7535043,3a,53.2y,35.48h,121.31t/data=!3m7!1e1!3m5!1sHBna55BLQLIajk_OCmzSmw!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-31.310886978571915%26panoid%3DHBna55BLQLIajk_OCmzSmw%26yaw%3D35.48325260493627!7i16384!8i8192",
      pano: "HBna55BLQLIajk_OCmzSmw",
      lat: 26.2833499,
      lng: 127.7535043,
      heading: 35.48325260493627,
      pitch: 31.310886978571915,
      capturedLabel: {
        en: "Live · Google Street View",
        ja: "ライブ · Google ストリートビュー",
      },
    },
  },
  {
    slug: "yokosuka-base",
    region: "yokosuka",
    title: {
      en: "Yokosuka — Headquarters",
      ja: "横須賀 — 本社拠点",
    },
    location: {
      en: "Yokosuka, Kanagawa",
      ja: "神奈川県横須賀市",
    },
    route: null,
    status: "live",
    nearest: {
      en: "Near U.S. Naval Base Yokosuka",
      ja: "米海軍横須賀基地周辺",
    },
    summary: {
      en: "Our home base. NoJo Enterprise was founded in Yokosuka in 2013, and the Yokosuka signboard placement is where the company's outdoor presence first took root. Currently visible on a rooftop building near Route 16, the National Highway 16 corridor toward Yokosuka.",
      ja: "私たちのホームベース。NoJo Enterpriseは2013年に横須賀で創業し、横須賀の看板設置がNoJoの屋外プレゼンスの原点となっています。現在、横須賀方面に向かう国道16号線沿いのビル屋上に設置されており、ドライバーから視認できます。",
    },
    photos: [
      "/signboards/yokosuka/yokosuka-route16-01.jpg",
      "/signboards/yokosuka/yokosuka-1.jpg",
      "/signboards/yokosuka/yokosuka-3.jpg",
      "/signboards/yokosuka/yokosuka-4.jpg",
      "/signboards/yokosuka/yokosuka-5.jpg",
      "/signboards/yokosuka/yokosuka-6.jpg",
    ],
    streetView: {
      url: "https://www.google.com/maps/@35.2822315,139.6601223,3a,17.6y,164.03h,108.65t/data=!3m7!1e1!3m5!1syArHPqS1HXxlJhPEEfbhkQ!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-18.65424147863834%26panoid%3DyArHPqS1HXxlJhPEEfbhkQ%26yaw%3D164.0338152468887!7i16384!8i8192",
      pano: "yArHPqS1HXxlJhPEEfbhkQ",
      lat: 35.2822315,
      lng: 139.6601223,
      heading: 164.0338152468887,
      pitch: 18.65424147863834,
      capturedLabel: {
        en: "Live · Google Street View",
        ja: "ライブ · Google ストリートビュー",
      },
    },
  },
];

export const REGIONS = [
  {
    key: "okinawa",
    en: "Okinawa",
    ja: "沖縄",
    accent: "#2F8F3C", // green — JTR brand color, fits the tropical setting
    description: {
      en: "Three positions along Route 58 — Okinawa's main highway — covering Camp Foster, Oyama and central island stretches.",
      ja: "沖縄本島の主要幹線、国道58号線沿いに設置された3区画。キャンプ・フォスター、大山、本島中部をカバーします。",
    },
  },
  {
    key: "yokosuka",
    en: "Yokosuka",
    ja: "横須賀",
    accent: "#0E5FB0", // blue — NoJo HQ
    description: {
      en: "Our home base in Kanagawa — where NoJo Enterprise was founded in 2013.",
      ja: "NoJo Enterprise創業の地、神奈川県横須賀市。",
    },
  },
];

export function getSignboardBySlug(slug) {
  return SIGNBOARDS.find((s) => s.slug === slug) || null;
}

export function getSignboardsByRegion(regionKey) {
  return SIGNBOARDS.filter((s) => s.region === regionKey);
}

/**
 * Build a Google Maps Street View embed iframe `src` from the data we
 * extracted from a regular Maps URL (panoid + lat/lng + heading/pitch).
 *
 * This uses Google's official no-API-key embed format
 * (`https://www.google.com/maps/embed?pb=...`) — the same one Google
 * generates when you click "Share or embed map → Embed a map" in
 * Maps. The `pb` payload is a `!`-delimited series of fields; the
 * Street View slice is `!6m8!1m7!1s<PANO>!2m2!1d<LAT>!2d<LNG>!3f<HEADING>!4f<PITCH>!5f<FOV>`.
 *
 * If a board's `streetView.embed` field is explicitly set, callers
 * should prefer that value (it'll be the verbatim Google-issued URL
 * with the real `!4v…` timestamp).
 */
export function buildStreetViewEmbedSrc(sv) {
  if (!sv) return null;
  if (sv.embed) return sv.embed;
  if (!sv.pano || sv.lat == null || sv.lng == null) return null;
  const heading = sv.heading ?? 0;
  const pitch = sv.pitch ?? 0;
  const fov = sv.fov ?? 0.7820865974627469;
  // Note: Google's `pb` field puts longitude before latitude in some
  // positions and latitude before longitude in others. For the Street
  // View `!6m8!1m7!1s…!2m2!1d<lat>!2d<lng>` pano section, the order
  // is lat then lng.
  const pb = `!6m8!1m7!1s${sv.pano}!2m2!1d${sv.lat}!2d${sv.lng}!3f${heading}!4f${pitch}!5f${fov}`;
  return `https://www.google.com/maps/embed?pb=${pb}`;
}
